/**
 * RecentlyViewed LiveView Hook
 *
 * Consolidated hook that handles both:
 * 1. Loading recently viewed products from localStorage (homepage)
 * 2. Saving new product views from server events (product pages)
 *
 * Storage format: Array of objects with:
 * - asin: string
 * - domain_id: number
 * - title: string
 * - image_url: string
 * - current_price: string (decimal)
 * - timestamp: number (unix ms)
 */

const STORAGE_KEY = 'tosheroon_recently_viewed';
const MAX_ITEMS = 12;

export const RecentlyViewed = {
  mounted() {
    // Load recently viewed from localStorage and push to server
    const items = this.loadFromStorage();
    if (items.length > 0) {
      this.pushEvent("recently_viewed_loaded", { products: items });
    }

    // Listen for product-viewed server event (from ProductLive mount)
    this.handleEvent("product-viewed", (product) => {
      this.addProduct(product);
    });

    // Listen for new product views from other LiveViews (via window event)
    this.handleWindowEvent = (e) => {
      this.addProduct(e.detail);
    };
    window.addEventListener('tosheroon:product-viewed', this.handleWindowEvent);
  },

  destroyed() {
    window.removeEventListener('tosheroon:product-viewed', this.handleWindowEvent);
  },

  loadFromStorage() {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      if (!data) return [];

      const items = JSON.parse(data);
      // Filter out items older than 30 days
      const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
      return items.filter(item => item.timestamp > thirtyDaysAgo);
    } catch (e) {
      console.error('Error loading recently viewed:', e);
      return [];
    }
  },

  saveToStorage(items) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      console.error('Error saving recently viewed:', e);
    }
  },

  addProduct(product) {
    if (!product.asin || !product.domain_id) return;

    const items = this.loadFromStorage();

    // Remove existing entry for this product (by asin + domain_id)
    const filtered = items.filter(
      item => !(item.asin === product.asin && item.domain_id === product.domain_id)
    );

    // Add new entry at the beginning
    const newItem = {
      asin: product.asin,
      domain_id: product.domain_id,
      title: product.title,
      image_url: product.image_url,
      current_price: product.current_price,
      timestamp: Date.now()
    };

    const updated = [newItem, ...filtered].slice(0, MAX_ITEMS);
    this.saveToStorage(updated);

    // Update the server with new list
    this.pushEvent("recently_viewed_loaded", { products: updated });
  }
};

/**
 * Helper function to emit a product-viewed event from ProductLive
 * Call this when a product page is mounted
 */
export function emitProductViewed(product) {
  window.dispatchEvent(new CustomEvent('tosheroon:product-viewed', {
    detail: product
  }));
}
